import React, { useState } from 'react';
import { mockData } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  FileText,
  Clipboard,
  BookOpen,
  Activity,
  FileSearch,
  Book,
  ListChecks,
  Stethoscope,
  MessageSquare,
  HelpCircle,
  Lightbulb,
  Users,
  CheckCircle,
  BadgeIndianRupee,
  Zap,
  Briefcase,
  Download,
  CheckCircle2,
  ArrowRight,
  CirclePlus,
  RefreshCw
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const iconMap = {
  FileText,
  Clipboard,
  BookOpen,
  Activity,
  FileSearch,
  Book,
  ListChecks,
  Stethoscope,
  MessageSquare,
  HelpCircle,
  Lightbulb,
  Users,
  CheckCircle,
  BadgeIndianRupee,
  Zap,
  Briefcase,
  RefreshCw
};

const Home = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleFreeSampleDownload = async () => {
    setIsDownloading(true);
    toast.info('Preparing your free sample PDF...');
    
    try {
      // Create a link element and trigger download
      const link = document.createElement('a');
      link.href = `${BACKEND_URL}/api/download/sample`;
      link.download = 'Medical_Coding_Sample.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => {
        setIsDownloading(false);
        toast.success('Sample PDF download started successfully!');
      }, 1000);
    } catch (error) {
      setIsDownloading(false);
      toast.error('Failed to download sample PDF. Please try again.');
    }
  };

  const handlePurchase = () => {
    setShowEmailModal(true);
  };

  const initiatePayment = async () => {
    if (!userEmail || !userEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      toast.info('Creating payment order...');
      
      // Create Razorpay order
      const response = await fetch(`${BACKEND_URL}/api/payment/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          amount: 19900 // ₹199
        })
      });

      const orderData = await response.json();

      // Razorpay checkout options
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.order_id,
        name: 'Medical Coder Pro',
        description: 'Medical Coding Interview Preparation Material',
        image: '/logo.png',
        prefill: {
          email: userEmail
        },
        theme: {
          color: '#2563eb'
        },
        handler: async function (response) {
          // Verify payment
          try {
            toast.info('Verifying payment...');
            
            const verifyResponse = await fetch(`${BACKEND_URL}/api/payment/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                email: userEmail
              })
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.status === 'success') {
              toast.success('Payment successful! Downloading PDF...');
              
              if (verifyData.email_sent) {
                toast.success('✅ PDF has been sent to your email!', { duration: 5000 });
              }
              
              setShowEmailModal(false);
              
              // Download full version PDF
              setTimeout(() => {
                const link = document.createElement('a');
                link.href = `${BACKEND_URL}/api/download/full?email=${encodeURIComponent(userEmail)}`;
                link.download = 'Medical_Coding_Full_Version.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                toast.success('PDF downloaded! Check your downloads folder and email.', { duration: 6000 });
              }, 1000);
            }
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: function() {
            toast.error('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      toast.error('Failed to initiate payment. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img src="/logo.png" alt="Medical Coder Pro Logo" className="h-10 w-10 object-contain" />
              <h1 className="text-2xl font-bold text-blue-900">Medical Coder Pro</h1>
            </div>
            <Button onClick={handlePurchase} className="bg-blue-600 hover:bg-blue-700 text-white">
              Buy Now @ ₹199
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-6 bg-blue-100 text-blue-800 hover:bg-blue-200 px-4 py-2 text-sm font-medium">
            Trusted by Medical Coding Freshers Across India
          </Badge>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-blue-900 mb-6 leading-tight">
            {mockData.hero.headline}
          </h2>
          <p className="text-xl text-gray-700 mb-10 max-w-3xl mx-auto">
            {mockData.hero.subheadline}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
            <Button 
              onClick={handleFreeSampleDownload} 
              size="lg" 
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-5 py-4 text-base font-semibold w-full sm:w-auto whitespace-normal leading-tight"
              disabled={isDownloading}
            >
              <Download className="mr-2 h-5 w-5 flex-shrink-0" />
              <span>{mockData.hero.ctaPrimary}</span>
            </Button>
            <Button 
              onClick={handlePurchase}
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-4 text-base font-semibold shadow-lg w-full sm:w-auto whitespace-nowrap"
            >
              <span>Buy Full Version – ₹199 Only</span>
              <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" />
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-blue-900 mb-6">{mockData.about.title}</h3>
          <p className="text-lg text-gray-700 leading-relaxed">
            {mockData.about.description}
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-blue-900 mb-4">What&apos;s Included</h3>
            <p className="text-lg text-gray-600">Everything you need to ace your medical coding interview</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {mockData.features.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <Card key={feature.id} className="border-blue-100 hover:shadow-lg hover:border-blue-300 transition-all duration-300">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <IconComponent className="h-5 w-5 text-blue-600" />
                      </div>
                      <h4 className="text-base font-semibold text-gray-800 leading-tight">{feature.title}</h4>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Free Sample Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-50">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-blue-200 shadow-xl">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Download className="h-8 w-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl text-blue-900">{mockData.freeSample.title}</CardTitle>
              <CardDescription className="text-base">
                <span className="font-semibold text-blue-700">{mockData.freeSample.format}</span> • {mockData.freeSample.note}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center pb-8 px-6">
              <Button 
                onClick={handleFreeSampleDownload}
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-base font-semibold w-full sm:w-auto"
                disabled={isDownloading}
              >
                <Download className="mr-2 h-5 w-5" />
                {mockData.freeSample.cta}
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-lg mx-auto">
          <Card className="border-2 border-blue-300 shadow-2xl relative overflow-visible">
            <div className="absolute -top-3 -right-3 bg-red-500 text-white px-6 py-2 text-base font-bold shadow-lg rounded-md z-10">
              SAVE ₹700
            </div>
            <CardHeader className="text-center pb-4 bg-blue-600 text-white">
              <CardTitle className="text-3xl">Full Version</CardTitle>
              <CardDescription className="text-blue-100 text-base mt-2">
                Complete Interview Preparation Material
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-8 pb-8 text-center">
              <div className="mb-6">
                <p className="text-gray-500 text-lg line-through mb-2">
                  Original Price: ₹{mockData.pricing.originalPrice}
                </p>
                <p className="text-5xl font-bold text-blue-900 mb-2">
                  ₹{mockData.pricing.offerPrice}
                </p>
                <Badge className="bg-red-100 text-red-700 hover:bg-red-200 px-3 py-1">
                  {mockData.pricing.offerText}
                </Badge>
              </div>
              <Button 
                onClick={handlePurchase}
                size="lg" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold"
              >
                {mockData.pricing.cta}
              </Button>
              <p className="text-sm text-gray-500 mt-4">Instant digital download • Lifetime access</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-blue-900 mb-4">Why Choose Medical Coder Pro</h3>
            <p className="text-lg text-gray-600">The smart choice for your medical coding career</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.whyChoose.map((item) => {
              const IconComponent = iconMap[item.icon];
              return (
                <div key={item.id} className="flex flex-col items-center text-center p-6 rounded-lg hover:bg-blue-50 transition-colors duration-300">
                  <div className="bg-blue-100 p-4 rounded-full mb-4">
                    <IconComponent className="h-8 w-8 text-blue-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-blue-900 mb-4">Frequently Asked Questions</h3>
            <p className="text-lg text-gray-600">Everything you need to know about Medical Coder Pro</p>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {mockData.faqs.map((faq) => (
              <AccordionItem 
                key={faq.id} 
                value={`item-${faq.id}`}
                className="bg-white border border-blue-100 rounded-lg px-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600 py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pb-4 whitespace-pre-line leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Refund Policy Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-2xl text-red-900 flex items-center gap-2">
                <HelpCircle className="h-6 w-6" />
                Refund Policy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-800 leading-relaxed">
                Due to the digital nature of the product (PDF), <strong>refunds are not available once the PDF is delivered.</strong>
              </p>
              <p className="text-gray-600 mt-3 text-sm">
                We recommend downloading the free sample PDF before purchasing to ensure the material meets your expectations.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl font-bold mb-4">{mockData.finalCTA.title}</h3>
          <p className="text-xl mb-8 text-blue-100">{mockData.finalCTA.subtitle}</p>
          <Button 
            onClick={handlePurchase}
            size="lg" 
            className="bg-white text-blue-600 hover:bg-blue-50 px-10 py-6 text-lg font-semibold shadow-xl"
          >
            {mockData.finalCTA.cta}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/logo.png" alt="Medical Coder Pro Logo" className="h-8 w-8 object-contain" />
                <h4 className="text-xl font-bold">Medical Coder Pro</h4>
              </div>
              <p className="text-gray-400">Your trusted partner for medical coding interview preparation</p>
            </div>
            <div>
              <h5 className="font-semibold mb-3">Product</h5>
              <ul className="space-y-2 text-gray-400">
                <li className="hover:text-white cursor-pointer transition-colors">Free Sample</li>
                <li className="hover:text-white cursor-pointer transition-colors">Full Version</li>
                <li className="hover:text-white cursor-pointer transition-colors">Features</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-3">Support</h5>
              <ul className="space-y-2 text-gray-400">
                <li className="hover:text-white cursor-pointer transition-colors">Contact Us</li>
                <li className="hover:text-white cursor-pointer transition-colors">FAQ</li>
                <li className="hover:text-white cursor-pointer transition-colors">Refund Policy</li>
              </ul>
              <div className="mt-4">
                <p className="text-sm font-semibold text-white mb-2">Email Support:</p>
                <a 
                  href="mailto:medicalcoderpro.in@gmail.com" 
                  className="text-blue-400 hover:text-blue-300 transition-colors text-sm break-all"
                >
                  medicalcoderpro.in@gmail.com
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400 mb-2">© 2025 Medical Coder Pro. All rights reserved.</p>
            <p className="text-sm text-blue-400 mb-2">www.medicalcoderpro.in</p>
            <p className="text-sm text-gray-500">Digital Product • Instant Download • Secure Payment</p>
          </div>
        </div>
      </footer>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enter Your Email</DialogTitle>
            <DialogDescription>
              We'll send you the download link after successful payment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="your.email@example.com"
              autoFocus
            />
            <div className="flex gap-3">
              <Button
                onClick={() => setShowEmailModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={initiatePayment}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Proceed to Pay ₹199
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
